import { useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type AiBillingGroup, aiApi, num } from '@/lib/aiAdmin';
import { formatCount, formatUsd } from '@/lib/format';

import { CENTER, columns, Loading, LoadFailed, type Organizations, SectionLabel, TITLE, useLoad } from './common';

const GROUPS: Array<{ id: AiBillingGroup; label: string; column: string }> = [
  { id: 'product', label: 'Product', column: 'Product' },
  { id: 'app', label: 'App', column: 'App' },
  { id: 'user', label: 'Person', column: 'Person' },
  { id: 'integration', label: 'Integration', column: 'Integration' },
];

const monthLabel = (iso: string) => new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(iso));

/** This month's spend of one organization (UTC calendar month), grouped by product, app, person or integration. */
export function UsageTab({ orgs }: { orgs: Organizations }) {
  const [organizationId, setOrganizationId] = useState('');
  const [groupBy, setGroupBy] = useState<AiBillingGroup>('product');
  const load = useLoad(async () => (organizationId ? { organizationId, groupBy, ...(await aiApi.billingUsage(organizationId, groupBy)) } : undefined), [organizationId, groupBy]);
  // The load keeps the old answer while the new one comes, so rows of another organization or group are never shown.
  const data = load.data?.organizationId === organizationId && load.data.groupBy === groupBy ? load.data : undefined;
  const group = GROUPS.find((option) => option.id === groupBy)!;
  const rows = [...(data?.rows ?? [])].sort((a, b) => num(b.chargeUsd) - num(a.chargeUsd));
  const total = rows.reduce((sum, row) => sum + num(row.chargeUsd), 0);

  return (
    <div className="space-y-4">
      <SectionLabel
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {orgs.error ? null : (
              <Select value={organizationId} onValueChange={setOrganizationId}>
                <SelectTrigger size="sm" className="w-64" aria-label="Organization">
                  <SelectValue placeholder="Choose an organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.rows.map((row) => (
                    <SelectItem key={row.organizationId} value={row.organizationId}>
                      {orgs.nameOf(row.organizationId)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <SegmentedFilter label="Group by" options={GROUPS} value={groupBy} onChange={setGroupBy} />
          </div>
        }
      >
        Usage this month
      </SectionLabel>
      {orgs.error ? (
        <LoadFailed message={`The organizations could not be loaded: ${orgs.error}`} onRetry={orgs.reload} />
      ) : !organizationId ? (
        <p className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">Choose an organization to see its spend this month.</p>
      ) : load.error ? (
        <LoadFailed message={load.error} onRetry={load.reload} />
      ) : !data ? (
        <Loading label="Loading usage" />
      ) : (
        <DataTable
          footer={
            <p className="border-t border-border px-4 py-3 text-sm tabular-nums">
              <span className="text-muted-foreground">Total for {monthLabel(data.from)} (UTC): </span>
              <span className="font-medium">{formatUsd(total)}</span>
            </p>
          }
        >
          <DataTableHead columns={columns([group.column, 'Events', 'Charge'])} />
          <tbody>
            {rows.map((row) => (
              <DataTableRow key={row.key}>
                <DataTableCell className={TITLE}>
                  <span title={row.key}>{row.email || row.key || (groupBy === 'user' ? 'No person (the app is billed)' : 'None')}</span>
                </DataTableCell>
                <DataTableCell className={`${CENTER} tabular-nums`}>{formatCount(num(row.events))}</DataTableCell>
                <DataTableCell className={`${CENTER} tabular-nums`}>{formatUsd(num(row.chargeUsd))}</DataTableCell>
              </DataTableRow>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No usage this month.
                </td>
              </tr>
            ) : null}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}
